import { ApiResponse } from '@/types/api.types';
import baseApi from './RootBaseApi';
import {
  Booking,
  PaginatedBookingResponse,
  AvailableBerth,
  BookingNoShowSummary,
} from '@/types/booking-management.types';
import { RefundMode } from '@/constant';
import { BerthResponse } from '@/types/berth-management.types';

export const BookingService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAvailableBerths: builder.query<
      ApiResponse<AvailableBerth[]>,
      {
        harbor_id?: string;
        category_id?: string;
        berth_type?: number;
        start_date?: string;
        end_date?: string;
        search?: string;
        page?: number;
        limit?: number;
      }
    >({
      query: (params) => ({
        url: 'berths/available',
        method: 'GET',
        params,
      }),
      transformResponse: (response: ApiResponse<AvailableBerth[]>) => response,
      providesTags: ['Bookings'],
    }),

    getBlockedBerths: builder.query<
      BerthResponse[],
      {
        harbor_id?: string;
        category_id?: string;
        search?: string;
        page?: number;
        limit?: number;
      }
    >({
      query: (params) => ({
        url: 'berths/blocked',
        method: 'GET',
        params,
      }),
      transformResponse: (response: BerthResponse[]) => response,
      providesTags: ['Bookings'],
    }),

    getExternallyReservedBerths: builder.query<
      BerthResponse[],
      {
        harbor_id?: string;
        category_id?: string;
        search?: string;
        page?: number;
        limit?: number;
      }
    >({
      query: (params) => ({
        url: 'berths/externally-reserved',
        method: 'GET',
        params,
      }),
      transformResponse: (response: BerthResponse[]) => response,
      providesTags: ['Bookings'],
    }),

    getAllBookings: builder.query<
      PaginatedBookingResponse,
      {
        page?: number;
        limit?: number;
        status?: number;
        search?: string;
        harbor_id?: string;
        start_date?: string;
        end_date?: string;
        from?: string;
        to?: string;
        guest_status?: 'guest_unclaimed' | 'guest_claimed';
      }
    >({
      query: (params) => ({
        url: 'bookings',
        method: 'GET',
        params: {
          page: params.page,
          limit: params.limit,
          status: params.status,
          search: params.search,
          harbor_id: params.harbor_id,
          start_date: params.start_date,
          end_date: params.end_date,
          from: params.from,
          to: params.to,
        },
      }),
      transformResponse: (response: PaginatedBookingResponse) => response,
      providesTags: ['Bookings'],
    }),

    exportBookingsCsv: builder.query<
      Blob | { fileUrl?: string; url?: string; data?: { fileUrl?: string } },
      {
        harbor_id?: string;
        status?: number;
        search?: string;
        start_date?: string;
        end_date?: string;
        from?: string;
        to?: string;
      }
    >({
      query: (params) => ({
        url: 'bookings/export/csv',
        method: 'GET',
        params: {
          harbor_id: params.harbor_id,
          status: params.status,
          search: params.search,
          start_date: params.start_date,
          end_date: params.end_date,
          from: params.from,
          to: params.to,
        },
        responseType: 'blob',
      }),
      transformResponse: (
        response: Blob | { fileUrl?: string; url?: string; data?: { fileUrl?: string } }
      ) => response,
      keepUnusedDataFor: 0,
    }),

    getBookingById: builder.query<ApiResponse<Booking>, string>({
      query: (id) => ({
        url: `bookings/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<Booking>) => response,
    }),

    updateBookingStatus: builder.mutation<
      ApiResponse<Booking>,
      {
        id: string;
        update: { status: number; cancellation_reason?: string };
      }
    >({
      query: ({ id, update }) => ({
        url: `bookings/${id}/status`,
        method: 'PATCH',
        data: update,
      }),
      transformResponse: (response: ApiResponse<Booking>) => response,
      invalidatesTags: ['Bookings'],
    }),

    updateBooking: builder.mutation<
      ApiResponse<Booking & { conflictingBookings: boolean }>,
      {
        id: string;
        update: {
          start_date?: string;
          end_date?: string;
          status?: number;
          berth_id?: string;
        };
      }
    >({
      query: ({ id, update }) => ({
        url: `bookings/${id}/admin-update`,
        method: 'PATCH',
        data: update,
      }),
      transformResponse: (
        response: ApiResponse<Booking & { conflictingBookings: boolean }>
      ) => response,
      invalidatesTags: ['Bookings'],
    }),

    cancelBooking: builder.mutation<
      ApiResponse<Booking>,
      { id: string; data: { status: number; cancellation_reason?: string } }
    >({
      query: ({ id, data }) => ({
        url: `bookings/${id}/cancel`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: ApiResponse<Booking>) => response,
      invalidatesTags: ['Bookings'],
    }),

    getBookingNoShowSummary: builder.mutation<
      ApiResponse<BookingNoShowSummary>,
      string
    >({
      query: (id) => ({
        url: `bookings/${id}/no-show-summary`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<BookingNoShowSummary>) =>
        response,
    }),

    markBookingNoShow: builder.mutation<
      ApiResponse<Booking>,
      { id: string; data: { mode: RefundMode; note: string } }
    >({
      query: ({ id, data }) => ({
        url: `bookings/${id}/no-show`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: ApiResponse<Booking>) => response,
      invalidatesTags: ['Bookings'],
    }),
  }),
});

export const {
  useGetAllBookingsQuery,
  useGetBookingByIdQuery,
  useUpdateBookingStatusMutation,
  useUpdateBookingMutation,
  useCancelBookingMutation,
  useGetAvailableBerthsQuery,
  useGetBlockedBerthsQuery,
  useGetExternallyReservedBerthsQuery,
  useGetBookingNoShowSummaryMutation,
  useMarkBookingNoShowMutation,
  useLazyExportBookingsCsvQuery,
} = BookingService;
